import { json2csv } from 'json-2-csv';
import customTheme from '../components/customTheme';
import { CONSTANTS } from '../constants/constants';
import { availableFilterOperations } from '../components/NRReactGrid/NRReactGrid.utils';
const { UNDETERMINED, NEGLIGIBLE, CONSERVATIVE, MODERATE, SIGNIFICANT, SEVERE } = CONSTANTS.RISK_METER;

export const isEmpty = obj => !obj || (Object.keys(obj).length === 0 && obj.constructor === Object);
export const transformToQueryParams = params =>
  !isEmpty(params)
    ? `?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`
    : '';
export const findNode = (id, currentNode) => {
  let i, currentChild, result;

  if (id === currentNode.id) {
    return currentNode;
  } else if (!!currentNode.children?.length) {
    // Use a for loop instead of forEach to avoid nested functions
    // Otherwise "return" will not work properly
    for (i = 0; i < currentNode.children.length; i += 1) {
      currentChild = currentNode.children[i];

      // Search in the current child
      result = findNode(id, currentChild);

      // Return the result if the node has been found
      if (result !== false) {
        return result;
      }
    }

    // The node has not been found and we have no more options
    return false;
  } else {
    return false;
  }
};

export const findNodeByPath = (path, currentNode) => {
  let i, currentChild, result;

  if (path === currentNode.path) {
    return currentNode;
  } else if (!!currentNode.children?.length) {
    // Use a for loop instead of forEach to avoid nested functions
    // Otherwise "return" will not work properly
    for (i = 0; i < currentNode.children.length; i += 1) {
      currentChild = currentNode.children[i];

      // Search in the current child
      result = findNodeByPath(path, currentChild);

      // Return the result if the node has been found
      if (result !== false) {
        return result;
      }
    }

    // The node has not been found and we have no more options
    return false;
  } else {
    return false;
  }
};

export const findNodeByFilePath = (filePath, currentNode) => {
  let i, currentChild, result;

  if (filePath === currentNode.path) {
    return currentNode;
  } else if (!!currentNode.children?.length) {
    // Use a for loop instead of forEach to avoid nested functions
    // Otherwise "return" will not work properly
    for (i = 0; i < currentNode.children.length; i += 1) {
      currentChild = currentNode.children[i];

      // Search in the current child
      result = findNodeByFilePath(filePath, currentChild);

      // Return the result if the node has been found
      if (result !== false) {
        return result;
      }
    }

    // The node has not been found and we have no more options
    return false;
  } else {
    return false;
  }
};

export const ArrayMove = (arr, old_index, new_index) => {
  while (old_index < 0) {
    old_index += arr.length;
  }
  while (new_index < 0) {
    new_index += arr.length;
  }
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
};

export const splitCamelCase = str => {
  return str.replace(/([A-Z])/g, ' $1');
};

export const splitCamelCaseWithInitials = str => {
  return str.replace(/([A-Z])/g, ' $1') + ` (${str.replace(/([a-z])/g, '')})`;
};

/** Two-letter (or short) initials for user avatars from display name and/or email. */
export function getUserDisplayInitials(displayName, email) {
  const source = (displayName || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export const humanFileSize = (bytes, si = true, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
};

export function getProp(object, keys, defaultVal) {
  keys = Array.isArray(keys) ? keys : keys.split('.');
  object = object[keys[0]];
  if (object && keys.length > 1) {
    return getProp(object, keys.slice(1), defaultVal);
  }
  return object === undefined ? defaultVal : object;
}

export function clearTypename(obj) {
  if (!obj) return obj;
  const newObj = {};
  Object.keys(obj).forEach(k => {
    if (k !== '__typename') {
      newObj[k] = obj[k];
    }
  });
  return newObj;
}

export function cloneDeep(entity, cache = new WeakMap()) {
  const referenceTypes = ['Array', 'Object', 'Map', 'Set', 'WeakMap', 'WeakSet'];
  const entityType = Object.prototype.toString.call(entity);
  if (!new RegExp(referenceTypes.join('|')).test(entityType)) return entity;
  if (cache.has(entity)) {
    return cache.get(entity);
  }
  const c = new entity.constructor();

  if (entity instanceof Map || entity instanceof WeakMap) {
    entity.forEach((value, key) => c.set(cloneDeep(key), cloneDeep(value)));
  }
  if (entity instanceof Set || entity instanceof WeakSet) {
    entity.forEach(value => c.add(cloneDeep(value)));
  }
  cache.set(entity, c);
  return Object.assign(c, ...Object.keys(entity).map(prop => ({ [prop]: cloneDeep(entity[prop], cache) })));
}

/**
 * Comparison function to order vulnerabilities by Critical, High, Medium and Low.
 * @param rowA
 * @param rowB
 * @returns {number}
 */
export const weightVulnerabilities = (rowA, rowB) => {
  const rowAnew = [rowA.critical.count, rowA.high.count, rowA.medium.count, rowA.low.count];
  const rowBnew = [rowB.critical.count, rowB.high.count, rowB.medium.count, rowB.low.count];
  const both = rowAnew.map((a, i) => {
    return [a, rowBnew[i]];
  });

  for (let i = 0; i < 4; i++) {
    if (both[i][0] != both[i][1]) {
      return both[i][0] - both[i][1];
    }
  }

  return 0;
};

/**
 * Round numbers
 * for examples refer to https://stackoverflow.com/questions/7342957/how-do-you-round-to-1-decimal-place-in-javascript
 */
export const round = (value, precision) => {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
};

/**
 * Relative percent change from baseline to current.
 * Positive = increase, negative = decrease (capped at -100% when current is 0).
 */
export function calculateRelativePercentChange(current, baseline) {
  const a = current || 0;
  const b = baseline || 0;

  if (b === 0) {
    return a === 0 ? 0 : 100;
  }

  return Math.round(((a - b) / b) * 100);
}

export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string') return str;
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
};

export const snakeCaseToHuman = (value, { preserveAcronyms = true } = {}) => {
  if (typeof value !== 'string') return value;
  const s = value.trim();
  if (!s) return s;

  return s
    .split('_')
    .filter(Boolean)
    .map(part => {
      const token = part.trim();
      if (!token) return '';

      // Preserve acronyms like "CRM", "API", "SSO" (either already uppercase or short tokens)
      if (preserveAcronyms && (/^[A-Z0-9]+$/.test(token) || token.length <= 3)) {
        return token.toUpperCase();
      }

      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .filter(Boolean)
    .join(' ');
};

export const capitalizeFirstWord = str => {
  return !!str
    ? str
        .split(' ')
        .map(e => capitalizeFirstLetter(e))
        .join(' ')
    : str;
};

export const capitalizeFirstWordPreserveAcronyms = str => {
  return !!str
    ? str
        .split(' ')
        .map(e => (e.length <= 3 && /^[A-Z0-9]+$/.test(e) ? e : capitalizeFirstLetter(e)))
        .join(' ')
    : str;
};

export const validateEmail = email => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function abbreviate(number, maxPlaces, forcePlaces, forceLetter) {
  //number = Number(number);
  forceLetter = forceLetter || false;
  if (forceLetter !== false) {
    return annotate(number, maxPlaces, forcePlaces, forceLetter);
  }
  let abbr;
  if (number >= 1e12) {
    abbr = 'T';
  } else if (number >= 1e9) {
    abbr = 'B';
  } else if (number >= 1e6) {
    abbr = 'M';
  } else if (number >= 1e3) {
    abbr = 'K';
  } else {
    abbr = '';
  }
  return annotate(number, maxPlaces, forcePlaces, abbr);
}

function annotate(number, maxPlaces, forcePlaces, abbr) {
  // set places to false to not round
  let rounded = 0;
  switch (abbr) {
    case 'T':
      rounded = number / 1e12;
      break;
    case 'B':
      rounded = number / 1e9;
      break;
    case 'M':
      rounded = number / 1e6;
      break;
    case 'K':
      rounded = number / 1e3;
      break;
    case '':
      rounded = number;
      break;
  }
  if (maxPlaces !== false) {
    let test = new RegExp('\\.\\d{' + (maxPlaces + 1) + ',}$');
    if (test.test('' + rounded)) {
      rounded = rounded.toFixed(maxPlaces);
    }
  }
  if (forcePlaces !== false) {
    rounded = Number(rounded).toFixed(forcePlaces);
  }
  return rounded + abbr;
}

export function omit(obj, omitKey) {
  return Object.keys(obj).reduce((result, key) => {
    if (key !== omitKey) {
      result[key] = obj[key];
    }
    return result;
  }, {});
}

export const flattenObject = function (ob) {
  var toReturn = {};

  for (var i in ob) {
    if (!ob.hasOwnProperty(i)) continue;

    if (typeof ob[i] == 'object') {
      if (Array.isArray(ob[i])) {
        toReturn[i] = JSON.stringify(ob[i]);
      } else {
        var flatObject = flattenObject(ob[i]);
        for (var x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) continue;

          toReturn[`entry.${x}`] = flatObject[x];
        }
      }
    } else {
      toReturn[i] = ob[i];
    }
  }
  return toReturn;
};

function convertArrayOfObjectsToCSV(array) {
  let result;

  const columnDelimiter = ',';
  const lineDelimiter = '\n';
  const keys = Object.keys(array[0]);

  result = '';
  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  array.forEach(item => {
    let ctr = 0;
    keys.forEach(key => {
      if (ctr > 0) result += columnDelimiter;

      result += item[key];

      ctr++;
    });
    result += lineDelimiter;
  });

  return result;
}

/** Keys omitted from client-side CSV export (e.g. grid row metadata, not table data). */
const CSV_EXPORT_UNWANTED_KEYS = ['entry', 'actions'];

function getCSVKeys(array) {
  const isExportableKey = key => !key.startsWith('__') && !CSV_EXPORT_UNWANTED_KEYS.includes(key);

  let baseLevelKeys = Object.keys(array[0])
    .map(key => (isExportableKey(key) ? key : ''))
    .filter(prop => !!prop);

  let entryKeys = !!array[0].entry
    ? Object.keys(array[0].entry)
        .map(entryKey => {
          if (isExportableKey(entryKey) && !baseLevelKeys.includes(entryKey)) {
            return { field: `entry.${entryKey}`, title: entryKey };
          }
          return '';
        })
        .filter(prop => !!prop)
    : [];

  return [...baseLevelKeys, ...entryKeys];
}

export function copyAsCSV(array, filterUnwantedKeys = true) {
  let options = {
    delimiter: {
      wrap: '"', // Double Quote (") character
      field: ',', // Comma field delimiter
      eol: '\n' // Newline delimiter
    },
    ...(filterUnwantedKeys && { keys: getCSVKeys(array) })
  };
  json2csv(
    array,
    (err, data) => {
      navigator.clipboard.writeText(data);
    },
    options
  );
}

/**
 * Normalizes a single cell value for CSV output.
 * Arrays are joined, objects are stringified, null/undefined become empty strings.
 */
function normalizeCsvCellValue(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return value.filter(v => v != null && v !== '').join('; ');
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

/**
 * Client-side CSV export from an in-memory grid dataset.
 * Exports EVERY provided column (regardless of the user's show/hide state), using the
 * column header (`title.value`) and the column's raw/underlying value
 * (`getCellValue(row)` when defined, else `row[column.name]`) — not rendered React cells.
 */
export function downloadTableAsCSV(rows = [], columns = [], fileName = 'export.csv') {
  const exportableColumns = (columns || []).filter(column => column && column.name);
  if (!exportableColumns.length) return;

  const headers = exportableColumns.map(column => column.title?.value ?? column.name);

  const data = (rows || []).map(row => {
    const record = {};
    exportableColumns.forEach((column, index) => {
      const rawValue = typeof column.getCellValue === 'function' ? column.getCellValue(row) : row?.[column.name];
      record[headers[index]] = normalizeCsvCellValue(rawValue);
    });
    return record;
  });

  const options = {
    delimiter: {
      wrap: '"',
      field: ',',
      eol: '\n'
    },
    emptyFieldValue: '',
    keys: headers
  };

  const triggerDownload = csvString => {
    const csvWithHeaders = csvString && csvString.length ? csvString : headers.join(',');
    const blob = new Blob([csvWithHeaders], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName.toLowerCase().endsWith('.csv') ? fileName : `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!data.length) {
    triggerDownload('');
    return;
  }

  json2csv(
    data,
    (err, csvString) => {
      if (err) return;
      triggerDownload(csvString);
    },
    options
  );
}


export const generateRandom = () => Math.floor(Math.random() * 100) + 1;

export const isArrayEmpty = array => {
  if (!Array.isArray(array)) return true;
  for (let i = 0; i < array.length; i++) {
    if (array[i]) return false;
  }
  return true;
};

export const isAnyUndefined = array => {
  if (!Array.isArray(array)) return true;
  if (array.length === 0) return true;
  for (let i = 0; i < array.length; i++) {
    if (!array[i]) return true;
  }
  return false;
};

export const getGroupFromPurl = purl => {
  //"pkg:npm/merge-descriptors@1.0.1",
  const group = purl?.split(':')[1]?.split('/')[0]; // Group would be npm
  return group;
};

export const getNameFromPath = path => {
  return path.replace(/^.*[\\\/]/, '');
};

export const includesPermission = (permissions, permissionToCheck) => {
  return !!permissions && Array.isArray(permissions) && permissions.includes(permissionToCheck);
};

export const getWeightedColor = (i, theme = customTheme, disabled) => {
  if (disabled) return theme.palette.secondary.main;
  if (i < 0 || i > 10) {
    return theme.palette.neutral[200];
  }
  if (i === 0) {
    return theme.palette.severityPalette[0].main;
  } else if (i < 2) {
    return theme.palette.severityPalette[1].main;
  } else if (i < 4) {
    return theme.palette.severityPalette[2].main;
  } else if (i < 6) {
    return theme.palette.severityPalette[3].main;
  } else if (i < 8) {
    return theme.palette.severityPalette[4].main;
  } else if (i <= 10) {
    return theme.palette.severityPalette[5].main;
  } else {
    return theme.palette.neutral[200]; // change gray
  }
};

export const getWeightedColorAlt = (i, theme = customTheme, disabled) => {
  if (disabled) return theme.palette.secondary.main;
  if (i < 0 || i > 10) {
    return theme.palette.neutral[200];
  }
  if (i === 0) {
    return theme.palette.severityPalette[0].alt;
  } else if (i < 2) {
    return theme.palette.severityPalette[1].alt;
  } else if (i < 4) {
    return theme.palette.severityPalette[2].alt;
  } else if (i < 6) {
    return theme.palette.severityPalette[3].alt;
  } else if (i < 8) {
    return theme.palette.severityPalette[4].alt;
  } else if (i <= 10) {
    return theme.palette.severityPalette[5].alt;
  } else {
    return theme.palette.neutral[200]; // change gray
  }
};

export const getWeightedCategory = i => {
  if (i === 0) {
    return UNDETERMINED;
  } else if (i < 2) {
    return NEGLIGIBLE;
  } else if (i < 4) {
    return CONSERVATIVE;
  } else if (i < 6) {
    return MODERATE;
  } else if (i < 8) {
    return SIGNIFICANT;
  } else if (i <= 10) {
    return SEVERE;
  } else {
    return NEGLIGIBLE;
  }
};

export const transformDataAnalyticCryptoCount = resp => {
  const objKeys = [...resp.measures];
  const rows = [];
  resp.rows.map(row => {
    let parsedEntries = {};
    objKeys.forEach((key, index) => {
      parsedEntries[key] = row[index];
    });
    rows.push(parsedEntries);
  });
  return rows[0];
};

export const isObject = item => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

export const mergeDeep = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
};

export const isNumber = value => {
  return typeof value === 'number' && isFinite(value);
};

export const sumValuesFromObjectKeys = obj => {
  const values = Object.keys(clearTypename(obj)).map(k => {
    return obj[k];
  });
  return values.reduce((p, c) => {
    return p + c;
  }, 0);
};

export function camelCaseToHuman(name, { preserveAcronyms = true } = {}) {
  if (typeof name !== 'string') return name;
  const s = name.trim();
  if (!s) return s;

  // If it's ALL_CAPS snake_case, treat it as regular words (not acronyms).
  const isAllCapsSnake = /^[A-Z0-9_]+$/.test(s) && s.includes('_');
  if (s.includes('_')) return snakeCaseToHuman(s, { preserveAcronyms: isAllCapsSnake ? false : preserveAcronyms });
  if (s.includes('-')) return snakeCaseToHuman(s.replace(/-/g, '_'), { preserveAcronyms });

  // Split camelCase / PascalCase while keeping acronym runs together:
  const parts = s.match(/[A-Z]+(?=[A-Z][a-z]|[0-9]|\b)|[A-Z]?[a-z]+|[0-9]+/g) || [];

  return parts
    .filter(Boolean)
    .map(token => {
      if (preserveAcronyms && /^[A-Z0-9]+$/.test(token)) return token; // keep "HTTP", "SSO", "2FA"
      return capitalizeFirstLetter(token);
    })
    .join(' ');
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.substring(1);
}

export function clearEmptyValues(obj) {
  const newObj = { ...obj };
  Object.keys(obj).forEach(e => {
    if (!obj[e]) {
      delete newObj[e];
    }
  });
  return newObj;
}

export function deduplicateData(arr) {
  if (!arr?.length) return arr;
  return Array.from(new Set(arr.map(obj => JSON.stringify(obj)))).map(item => JSON.parse(item));
}

export function extractPartFromPathname() {
  const pathname = window.location.pathname;
  const segments = pathname.split('/');
  const complexSegment = segments[2];
  const assetID = complexSegment.split('%7C')[0];
  return assetID;
}

export const getDataFromComposedAssetId = composedAssetId => {
  return {
    assetId: composedAssetId.split('|')[0],
    projectId: composedAssetId.split('|')[1],
    runId: composedAssetId.split('|')[2],
    snapshotId: composedAssetId.split('|')[3]
  };
};

export const getDataFromTurbinePPComposedAssetId = composedAssetId => {
  return {
    assetId: composedAssetId?.split('|')[0],
    revision: +composedAssetId?.split('|')[1] || 0
  };
};

export const showAsPercentile = (value, toFixed = 1) => {
  if (!value) return value;
  return (value * 100).toFixed(toFixed) + ' %';
};

export function getTableFilterOperation(operation) {
  return availableFilterOperations.find(tableOp => operation.toLowerCase() === tableOp.toLowerCase()) || operation;
}

export function toBytes(inputStr) {
  const units = {
    b: 1,
    kb: 1024,
    mb: Math.pow(1024, 2),
    gb: Math.pow(1024, 3),
    tb: Math.pow(1024, 4)
    // Add more units as needed
  };

  // Remove any commas, then split the string into value and unit
  const [value, unit] = inputStr.replace(',', '.').toLowerCase().split(/\s+/);

  // Convert the value to a float (because of potential decimals) and multiply by the unit's byte equivalent
  return parseFloat(value) * (units[unit] || 0);
}

export const shortenPath = (path, maxWidth) => {
  if (path == null) return '';
  const charWidth = 6;
  const pathWidth = path?.length * charWidth || 0;
  if (pathWidth <= maxWidth) {
    return path;
  }

  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 1) return path; // Avoid shortening if there's only one folder

  const fileName = parts.pop();
  const maxLength = maxWidth / charWidth - fileName.length - 1; // -1 for the '/' before fileName
  const firstFolder = '/' + parts.shift(); // Keep the first folder
  let result = '';
  const ellipsis = '/.../';
  let middlePart = ''; //part between first folder and file name
  let partialLength = firstFolder.length + fileName.length + ellipsis.length;

  if (partialLength >= maxLength) {
    return ellipsis + fileName; //this will handle the case when the first folder is too long
  }

  // Keep adding parts until reaching max length
  for (let i = parts.length - 1; i >= 0; i--) {
    if (partialLength + parts[i].length + 1 > maxLength) {
      result = firstFolder + ellipsis + middlePart;
      break;
    }
    middlePart = parts[i] + '/' + middlePart;
    partialLength += middlePart.length;
  }

  // Add the last part (fileName)
  result += fileName;

  return result;
};

export function areArraysEqualAsSets(arr1, arr2) {
  return new Set(arr1).size === new Set(arr2).size && [...new Set(arr1)].every(value => new Set(arr2).has(value));
}

export function getFilterFieldKey(value, filterObj) {
  if (!value || !filterObj) return null;
  return Object.keys(filterObj).find(filterKey => filterKey.toLowerCase() === value.toLowerCase());
}

function parseFieldFilterFromContext(fieldFilter, fields) {
  return {
    columnName: getFilterFieldKey(fieldFilter.fieldName, fields),
    value: fieldFilter.value,
    operation: getTableFilterOperation(fieldFilter.operation)
  };
}

export function getFiltersFromContext(filters, fields) {
  return [...(!!filters && !!filters?.length ? filters?.map(item => parseFieldFilterFromContext(item, fields)) : [])];
}

export function compareAssetIdAlone(assetA, assetB) {
  return assetA?.split('|')[0] === assetB?.split('|')[0];
}

export function validatePurl(purl) {
  // --- Scheme check ---
  if (!purl.toLowerCase().startsWith('pkg:')) {
    return "PURL must start with scheme 'pkg:'";
  }

  // Warn if scheme is not lowercase
  const schemePart = purl.slice(0, 4); // first 4 chars "pkg:"
  if (schemePart !== 'pkg:') {
    return "Scheme must be lowercase 'pkg:'";
  }

  // Check for extra slashes after the colon
  const afterScheme = purl.slice(4); // remove 'pkg:'
  if (afterScheme.startsWith('/')) {
    return "Extra slashes after scheme ':' are not recommended. Remove them (e.g., 'pkg:maven/...').";
  }

  // Regex for main PURL structure
  const regex =
    /^pkg:(?:\/*)?([a-z][a-z0-9.-]*)(?:\/((?:[^/@?#:\s]+\/)*[^/@?#:\s]+))?\/([^/@?#:\s]+)(?:@([^@/?#:\s]+)(?=$|\?|#))?(?:\?([a-z0-9._-]+=[^&#=\s]+(?:&[a-z0-9._-]+=[^&#=\s]+)*))?(?:#([^?#\s]*))?$/;

  if (!regex.test(purl)) {
    return 'Invalid PURL structure, should be pkg:<type>/<namespace>/<name>@<version>?<qualifiers>#<subpath>';
  }

  const match = purl.match(regex);
  const type = match[1];
  const namespace = match[2];
  const name = match[3];
  const version = match[4];
  const qualifiers = match[5];
  const subpath = match[6];

  // --- Type validation ---
  if (!type) return 'Package type is missing';
  if (type.includes('%')) {
    return `Package type "${type}" must not be percent-encoded`;
  }
  if (!/^[a-zA-Z][a-zA-Z0-9.-]*$/.test(type)) {
    return `Package type "${type}" is invalid (must start with a letter and contain only ASCII letters, numbers, '.' or '-')`;
  }

  // --- Namespace validation ---
  if (namespace) {
    const segments = namespace.split('/').filter(s => s !== '');
    for (const seg of segments) {
      // Must be percent-encoded if contains unsafe characters
      try {
        const decoded = decodeURIComponent(seg);
        if (!decoded) {
          return `Namespace segment "${seg}" is empty after decoding`;
        }
        if (decoded.includes('/')) {
          return `Namespace segment "${seg}" must not contain '/' after decoding`;
        }
      } catch {
        return `Namespace segment "${seg}" has invalid percent-encoding`;
      }
    }
  }

  // --- Name validation ---
  if (!name) return 'Package name is missing';

  // --- Version validation ---
  if (version && version.includes('@')) {
    return "Version cannot contain '@'";
  }
  if (version && /%(?![0-9A-Fa-f]{2})/.test(version)) {
    return 'Version contains invalid percent-encoding';
  }

  // --- Qualifiers validation ---
  if (qualifiers) {
    const keys = new Set();
    for (const q of qualifiers.split('&')) {
      const [k, v] = q.split('=');

      // Key rules
      if (!k) return 'Qualifier key is missing';
      if (!/^[a-z][a-z0-9._-]*$/.test(k)) {
        return `Qualifier key "${k}" is invalid (must start with [a-z] and contain only [a-z0-9._-])`;
      }
      if (k.includes('%')) {
        return `Qualifier key "${k}" must not be percent-encoded`;
      }

      // Value rules
      if (!v) return `Qualifier "${k}" has empty value`;
      if (/%(?![0-9A-Fa-f]{2})/.test(v)) {
        return `Qualifier value for "${k}" has invalid percent-encoding`;
      }
      if (!/^([A-Za-z0-9._~-]|%[0-9A-Fa-f]{2})+$/.test(v)) {
        return `Qualifier value for "${k}" contains invalid characters; use percent-encoding for special characters`;
      }

      // duplicate keys
      if (keys.has(k)) {
        return `Duplicate qualifier key "${k}"`;
      }
      keys.add(k);
    }
  }

  // --- Subpath validation ---
  if (subpath) {
    const segments = subpath.split('/');

    for (const s of segments) {
      if (s === '') {
        return 'Subpath contains empty segment';
      }
      if (/%(?![0-9A-Fa-f]{2})/.test(s)) {
        return `Subpath segment "${s}" has invalid percent-encoding`;
      }

      let decoded;
      try {
        decoded = decodeURIComponent(s);
      } catch {
        return `Subpath segment "${s}" has invalid percent-encoding sequence`;
      }

      if (decoded === '.' || decoded === '..') {
        return `Subpath segment "${s}" is invalid (cannot be '.' or '..')`;
      }

      if (decoded.includes('/')) {
        return `Subpath segment "${s}" contains an encoded slash (%2F), which is not allowed. Use separate segments instead (e.g., "#examples/test").`;
      }
    }
  }

  return true;
}

export const cpeRequirementsMessage = {
  title: 'CPE 2.3 strings must use the following requirements:',
  rules: [
    'The 3rd field (Part) must be one of: a, h, o',
    'The 4th field (Vendor) must not be: * or -',
    'The 5th field (Product) must not be: * or -',
    'The 6th field (Version) must not be: *',
    'Fields past the 6th field (Version) are allowed to contain: * and -',
    'All 13 fields must be populated'
  ]
};

export function validateCPE(cpe) {
  const regex = /^cpe:2\.3:[aho](?::[\p{L}\p{M}0-9._\-~*]+){10}$/u;

  if (!regex.test(cpe)) {
    return cpeRequirementsMessage;
  }

  return true;
}

// Convert 0 -> A, 1 -> B, ... 25 -> Z, 26 -> AA, etc.
export function indexToLetters(index) {
  let n = index + 1;
  let result = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

/**
 * Formats a signature string by splitting it into pairs of characters
 * and joining them with ':' and a zero-width space for better readability.
 * @param {string} signature - The signature string to format
 * @returns {string} - The formatted signature string, or empty string if signature is invalid
 */
export function formatSignature(signature) {
  if (!signature || signature === 'N/A') return '';
  const zeroWidthSpace = '\u200b';
  const splittedSignature = signature.match(/[\s\S]{1,2}/g) ?? [];
  return splittedSignature.length ? splittedSignature.join(`:${zeroWidthSpace}`) : '';
}

export function normalizeNewlines(value) {
  if (value == null) return '';

  return String(value)
    .replace(/\r\n/g, '\n') // Windows newlines -> \n
    .replace(/\\\\n/g, '\n') // \\n (two backslashes) -> newline
    .replace(/\\n/g, '\n'); // \n (one backslash) -> newline
}

export function truncateToDecimal(number = 0, decimalPlaces) {
  if (decimalPlaces <= 0) {
    return Math.trunc(number);
  }
  const factor = Math.pow(10, decimalPlaces);
  return Math.trunc(number * factor) / factor;
}

// Helper function to convert markdown to plain text using regex
export function markdownToPlainText(markdown) {
  try {
    let text = markdown;

    // Remove code blocks (```language\ncode\n```) - remove entirely
    text = text.replace(/```[\s\S]*?```/g, '');

    // Remove inline code (`code`) - keep the code content
    text = text.replace(/`([^`]+)`/g, '$1');

    // Remove markdown headers (# Header) - keep the header text
    text = text.replace(/^#{1,6}\s+(.+)$/gm, '$1');

    // Remove bold (**text** or __text__) - keep the text
    text = text.replace(/\*\*(.+?)\*\*/g, '$1');
    text = text.replace(/__(.+?)__/g, '$1');

    // Remove italic (*text* or _text_) - keep the text
    // Note: This must come after bold to avoid conflicts with **bold**
    text = text.replace(/\*([^*]+)\*/g, '$1');
    text = text.replace(/_([^_]+)_/g, '$1');

    // Remove links [text](url) - keep only the link text
    text = text.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');

    // Remove images ![alt](url) - keep only the alt text
    text = text.replace(/!\[([^\]]*)\]\([^\)]+\)/g, '$1');

    // Remove horizontal rules (--- or ***)
    text = text.replace(/^[-*]{3,}$/gm, '');

    // Remove blockquotes (> text) - keep the quoted text
    text = text.replace(/^>\s+(.+)$/gm, '$1');

    // Remove unordered list markers (- * +) - keep the list item text
    text = text.replace(/^[\s]*[-*+]\s+(.+)$/gm, '$1');

    // Remove ordered list markers (1. 2. etc.) - keep the list item text
    text = text.replace(/^[\s]*\d+\.\s+(.+)$/gm, '$1');

    // Clean up excessive newlines (more than 2 consecutive)
    text = text.replace(/\n{3,}/g, '\n\n');

    // Clean up leading/trailing whitespace
    return text.trim();
  } catch (error) {
    // Fallback: return original markdown if regex processing fails
    console.warn('Failed to strip markdown, returning original text:', error);
    return markdown;
  }
}
